
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Phone,
  Mail,
  MapPin, 
  Calendar,
  Edit,
  Trash2
} from 'lucide-react';
import type { Patient } from '../types/api';

interface PatientCardProps {
  patient: Patient;
  onEdit: () => void;
  onDelete: () => void;
  onClick: () => void;
}

const PatientCard: React.FC<PatientCardProps> = ({ patient, onEdit, onDelete, onClick }) => {
  const [showOrders, setShowOrders] = useState(false);

  const calculateAge = (dob: string) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };


  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking on action buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onClick();
  };


  return (
    <Card className="hover:shadow-lg transition-shadow border-0 shadow-md cursor-pointer border-l-4 border-l-emerald-500 shadow-emerald-100/50" onClick={handleCardClick}>
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
              {patient.first_name?.[0]?.toUpperCase()}{patient.last_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <CardTitle className="text-lg font-syne font-medium text-vitl-navy">
                {patient.first_name} {patient.last_name}
              </CardTitle>
              <p className="text-sm text-vitl-dark/70 font-lato">
                {patient.gender}, {patient.date_of_birth ? calculateAge(patient.date_of_birth) : 'Unknown age'} years old
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(); }} className="text-vitl-navy hover:text-emerald-600">
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-500 hover:text-red-700">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {patient.phone && (
            <div className="flex items-center space-x-2 text-sm font-lato">
              <Phone className="w-4 h-4 text-vitl-dark/50" />
              <span className="text-vitl-dark/70">{patient.phone}</span>
            </div>
          )}
          
          {patient.email && (
            <div className="flex items-center space-x-2 text-sm font-lato">
              <Mail className="w-4 h-4 text-vitl-dark/50" />
              <span className="text-vitl-dark/70">{patient.email}</span>
            </div>
          )}
          
          <div className="flex items-start space-x-2 text-sm font-lato">
            <MapPin className="w-4 h-4 text-vitl-dark/50 mt-0.5" />
            <span className="text-vitl-dark/70">
              {patient.address ? (
                <>
                  {patient.address.street?.join(', ')}<br />
                  {patient.address.city}, {patient.address.state} {patient.address.postal_code}
                </>
              ) : (
                <span className="italic text-gray-400">No address provided</span>
              )}
            </span>
          </div>
          
          <div className="flex items-center space-x-2 text-sm font-lato">
            <Calendar className="w-4 h-4 text-vitl-dark/50" />
            <span className="text-vitl-dark/70">DOB: {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : 'N/A'}</span>
          </div>

         
        </div>

        

        
      </CardContent>
    </Card>
  );
};

export default PatientCard;
